// Issue #72: AI（壁打ち）機能の有効/無効を単一の Context から配る。
// プロバイダ（CopilotLayout）と children（CopilotActions）で値がズレると
// useCopilotAction が throw するため、必ずこの Context 経由で参照すること。
// Issue #171: 管理画面での切替をリロードなしで反映するため、
// ページ遷移（pathname 変化、AiSettingsRouteSync）とタブ復帰（visibilitychange → visible）で再取得する。
// 値が変わらなければ state を更新しない（consumer の再レンダー・CopilotKit の作り直しを避ける）。
// 再取得に失敗した場合は直前の値を維持する（初回失敗のみ無効扱い）。
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { fetchAppSettings } from "../api/appSettings";

const INITIAL_STATE = { aiEnabled: false, isLoading: true };

// Provider 外で参照されても throw させない（白画面の誘発を防ぐ）。
// 取得中・取得失敗と同じく「無効」を既定とし、refresh は何もしない。
const DEFAULT_VALUE = { ...INITIAL_STATE, refresh: () => Promise.resolve() };

const AiSettingsContext = createContext(DEFAULT_VALUE);

export const AiSettingsProvider = ({ children }) => {
  const [state, setState] = useState(INITIAL_STATE);
  const mountedRef = useRef(false);

  const refresh = useCallback(
    () =>
      Promise.resolve()
        .then(() => fetchAppSettings())
        .then((settings) => {
          if (!mountedRef.current) return;
          const next = settings?.aiEnabled === true;
          setState((prev) =>
            !prev.isLoading && prev.aiEnabled === next
              ? prev
              : { aiEnabled: next, isLoading: false },
          );
        })
        .catch(() => {
          if (!mountedRef.current) return;
          setState((prev) =>
            prev.isLoading ? { aiEnabled: false, isLoading: false } : prev,
          );
        }),
    [],
  );

  useEffect(() => {
    mountedRef.current = true;
    refresh();

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refresh();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      mountedRef.current = false;
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [refresh]);

  const value = useMemo(() => ({ ...state, refresh }), [state, refresh]);

  return (
    <AiSettingsContext.Provider value={value}>
      {children}
    </AiSettingsContext.Provider>
  );
};

export const useAiSettings = () => useContext(AiSettingsContext);

// Issue #171: Router 内に置き、pathname が変わったら AI 設定を再取得する。
// 初回マウント時は Provider のマウント時取得と重複させないためスキップする。
// search / hash のみの変化では再取得しない。
export const AiSettingsRouteSync = () => {
  const { pathname } = useLocation();
  const { refresh } = useAiSettings();
  const previousPathRef = useRef(pathname);

  useEffect(() => {
    if (previousPathRef.current === pathname) return;
    previousPathRef.current = pathname;
    refresh();
  }, [pathname, refresh]);

  return null;
};

export default AiSettingsContext;
